import React, { useState } from 'react';
import { Eye, RotateCcw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SourceLine } from '@/components/SourceBadge';
import api from '@/lib/api';

export default function ReadingPractice() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('reveal_english');
  const [difficulty, setDifficulty] = useState(1);
  const [revealed, setRevealed] = useState({});
  const [started, setStarted] = useState(false);

  const loadPractice = async () => {
    setLoading(true);
    setRevealed({});
    try {
      // Pull from both Bible corpus and dictionary examples
      const [bibleRes, dictRes] = await Promise.all([
        api.get(`/bible/reading-practice?mode=${mode}&difficulty=${difficulty}&count=4`).catch(() => ({ data: { items: [] } })),
        api.get(`/bible/lesson-examples?difficulty=${difficulty}&limit=4`).catch(() => ({ data: { examples: [] } }))
      ]);

      const bibleItems = (bibleRes.data.items || []).map(item => ({
        ...item,
        id: item.verse_id || item.lesson_item_id,
        source_label: 'Literature',
      }));

      // Also pull dictionary example sentences
      const dictItems = (dictRes.data.examples || []).map(item => ({
        id: item.lesson_item_id || `dict_${Math.random().toString(36).slice(2, 8)}`,
        lamnso: item.lamnso,
        english: item.english || item.explanation,
        source: item.source,
        source_ref: item.source_ref,
        source_label: item.source === 'bible' ? 'Literature' : 'Dictionary',
        practice_mode: mode
      }));

      // Interleave and shuffle
      const all = [...bibleItems, ...dictItems];
      for (let i = all.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [all[i], all[j]] = [all[j], all[i]];
      }

      setItems(all.slice(0, 8));
      setStarted(true);
    } catch (err) { console.error(err) }
    setLoading(false);
  };

  const toggleReveal = (id) => {
    setRevealed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const modes = [
    { key: 'reveal_english', label: "Read Lamnso', reveal English" },
    { key: 'reveal_lamnso', label: "Read English, reveal Lamnso'" },
  ];
  const difficulties = [
    { key: 1, label: 'Short' },
    { key: 2, label: 'Medium' },
    { key: 3, label: 'Any length' },
  ];

  if (!started) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-safe">
        <div className="text-center mb-8">
          <Sparkles className="w-10 h-10 text-[#D4A373] mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="practice-title">
            Reading Practice
          </h1>
          <p className="text-sm text-[#57534E] mt-2">Practice reading Lamnso' with real bilingual sentences from literature and reference texts</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 mb-6" data-testid="practice-setup">
          <h3 className="text-sm font-semibold text-[#1C1917] mb-3">Choose your mode:</h3>
          <div className="space-y-2 mb-6">
            {modes.map(m => (
              <button key={m.key} onClick={() => setMode(m.key)}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${mode === m.key ? 'border-[#4CAF50] bg-[#E8F5E9]' : 'border-[#E7E5E4] hover:border-[#D4A373]'}`}
                data-testid={`mode-${m.key}`}>
                <span className="text-sm font-medium text-[#1C1917]">{m.label}</span>
              </button>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-[#1C1917] mb-3">Difficulty:</h3>
          <div className="flex gap-2 mb-6">
            {difficulties.map(d => (
              <button key={d.key} onClick={() => setDifficulty(d.key)}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-colors ${difficulty === d.key ? 'bg-[#4CAF50] text-white' : 'bg-[#F5F5F4] text-[#57534E] hover:bg-[#E7E5E4]'}`}
                data-testid={`difficulty-${d.key}`}>
                {d.label}
              </button>
            ))}
          </div>

          <Button onClick={loadPractice} className="w-full bg-[#4CAF50] hover:bg-[#3D8B40] text-white rounded-xl" data-testid="start-practice-btn">
            Start Practice
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-safe">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Reading Practice
        </h1>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-xl text-xs gap-1" onClick={loadPractice} data-testid="new-set-btn">
            <RotateCcw className="w-3 h-3" /> New Set
          </Button>
          <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={() => setStarted(false)} data-testid="change-mode-btn">
            Settings
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 border-4 border-[#E7E5E4] border-t-[#4CAF50] rounded-full animate-spin mx-auto" /></div>
      ) : (
        <div className="space-y-4" data-testid="practice-items">
          {items.map((item) => {
            const id = item.id;
            const isRevealed = revealed[id];
            const showFirst = mode === 'reveal_english' ? item.lamnso : item.english;
            const showSecond = mode === 'reveal_english' ? item.english : item.lamnso;
            const firstLabel = mode === 'reveal_english' ? "Lamnso'" : 'English';
            const secondLabel = mode === 'reveal_english' ? 'English' : "Lamnso'";

            if (!showFirst) return null;

            return (
              <div key={id} className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid={`practice-${id}`}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold text-[#A8A29E] uppercase tracking-wider">{firstLabel}</span>
                    <SourceLine source={item.source} detail={item.book_name_en ? `${item.book_name_en} ${item.chapter}:${item.verse}` : ''} />
                  </div>
                  <p className="text-sm text-[#1C1917] leading-relaxed">{showFirst}</p>
                </div>

                <button onClick={() => toggleReveal(id)}
                  className={`w-full p-4 border-t transition-colors ${isRevealed ? 'bg-[#FAFAF9]' : 'bg-[#F5F5F4] hover:bg-[#E7E5E4]'}`}
                  data-testid={`reveal-${id}`}>
                  {isRevealed ? (
                    <div>
                      <p className="text-[10px] font-bold text-[#D4A373] uppercase tracking-wider mb-1">{secondLabel}</p>
                      <p className={`text-sm leading-relaxed ${mode === 'reveal_english' ? 'text-[#57534E] italic' : 'text-[#1C1917]'}`}>
                        {showSecond}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-xs text-[#A8A29E]">
                      <Eye className="w-3.5 h-3.5" /> Tap to reveal {secondLabel}
                    </div>
                  )}
                </button>
              </div>
            );
          })}
          {items.length === 0 && (
            <p className="text-sm text-[#A8A29E] text-center py-8">No practice items available. Try a different difficulty.</p>
          )}
        </div>
      )}

      <p className="text-[10px] text-[#A8A29E] text-center mt-6">
        Bilingual context from literature and reference texts — not word-for-word alignment.
      </p>
    </div>
  );
}
